/**
 * HrV1CalibrationIndex.tsx
 *
 * Cross-cycle list of calibration sessions. Shows every cycle and the
 * state of its calibration step: preparing / in_session / locked / not yet
 * started. HR drills in for adjustments + locking.
 *
 * Route: /hr-v1/calibration (HR-role only)
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { routes } from '../routes';
import Button from '../components/ui/Button';
import CenterMessage from '../components/ui/CenterMessage';
import DisclosureBadge from '../components/epr/DisclosureBadge';
import WorkflowStepper from '../components/epr/WorkflowStepper';
import { useToast } from '../components/ui/Toast';
import {
  doc, getDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { listHrV1Cycles } from '../services/hrV1CycleService';
import { listSubmissionsForCycle } from '../services/eprSubmissionService';
import { listAppraisalsForCycle } from '../services/appraisalService';
import { listOpenAppealsForCycle } from '../services/appealService';
import { listOutcomesForCycle } from '../services/outcomeService';
import { deriveSteps } from '../services/hrV1StepDerivation';
import type { ReviewCycle } from '../types';
import type { CalibrationSession } from '../types/calibration';

interface Row {
  cycle: ReviewCycle;
  session: CalibrationSession | null;
  submissionCount: number;
  managerSubmittedCount: number;
  workflowSteps: ReturnType<typeof deriveSteps>;
}

const HrV1CalibrationIndex: React.FC = () => {
  const toast = useToast();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cycles = await listHrV1Cycles();
      const out: Row[] = [];
      for (const c of cycles) {
        try {
          const [sess, subs, apps, appeals, outcomes] = await Promise.all([
            getDoc(doc(db, 'calibrationSessions', c.id)),
            listSubmissionsForCycle(c.id),
            listAppraisalsForCycle(c.id),
            listOpenAppealsForCycle(c.id),
            listOutcomesForCycle(c.id),
          ]);
          const session = sess.exists()
            ? { id: sess.id, ...(sess.data() as Omit<CalibrationSession, 'id'>) }
            : null;
          const managerSubmittedCount = subs.filter(
            s => s.status === 'manager_submitted' || s.status === 'locked',
          ).length;
          const workflowSteps = deriveSteps({
            currentStep: c.currentStep ?? null,
            submissions: subs,
            appraisals: apps,
            session,
            openAppeals: appeals,
            outcomeCount: outcomes.length,
          });
          out.push({
            cycle: c,
            session,
            submissionCount: subs.length,
            managerSubmittedCount,
            workflowSteps,
          });
        } catch {
          // skip
        }
      }
      // Sort: those ready for calibration first, locked at the bottom
      out.sort((a, b) => priorityOf(a) - priorityOf(b));
      setRows(out);
    } catch (e) {
      toast.show(`Failed to load: ${(e as Error).message}`, { icon: 'error' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  if (loading) return <CenterMessage tone="info" title="Loading calibration sessions..." />;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <header>
        <div className="text-[11px] uppercase tracking-wider text-stone-500">HR Console · Step 4 of 7</div>
        <h1 className="text-2xl font-semibold text-stone-900 tracking-tight">Calibration — Bell-curve Normalization</h1>
        <p className="text-sm text-stone-600 mt-1">
          Step 4 of the review cycle. Normalises manager scores against the framework bell-curve <span className="text-stone-400">(5/15/60/15/5)</span> before sign-off.
          To start a brand-new cycle go to <Link to="/cycles" className="underline decoration-stone-300 hover:text-stone-900">Setup → Cycles</Link>.
        </p>
      </header>

      <CalibrationGuide />

      {rows.length === 0 ? (
        <CenterMessage
          tone="info"
          title="No cycles yet"
          body="Calibration only opens after Steps 1–3 finish (self-review, 360, manager submit). Start a new review cycle first."
          action={
            <Link to="/cycles">
              <Button variant="submit" icon="add">Start a new review cycle</Button>
            </Link>
          }
        />
      ) : (
        <div className="space-y-3">
          {rows.map(r => <CalibrationRow key={r.cycle.id} row={r} />)}
        </div>
      )}
    </div>
  );
};

// ─── Subcomponents ────────────────────────────────────────────────

const CalibrationRow: React.FC<{ row: Row }> = ({ row }) => {
  const c = row.cycle;
  const s = row.session;
  const ready = row.managerSubmittedCount > 0;
  const status = !s ? (ready ? 'ready' : 'waiting')
    : s.status === 'locked' ? 'locked'
    : s.status === 'in_session' ? 'in_session'
    : 'preparing';

  const tone: 'success' | 'info' | 'warning' | 'neutral' =
    status === 'locked' ? 'success'
    : status === 'in_session' || status === 'ready' ? 'info'
    : status === 'waiting' ? 'neutral'
    : 'warning';

  const label = ({
    ready: 'Ready to start',
    waiting: 'Waiting on submissions',
    preparing: 'Preparing',
    in_session: 'In session',
    locked: 'Locked',
  } as const)[status];

  return (
    <section className="rounded-xl border border-stone-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-semibold text-stone-900">{c.name}</h3>
            <DisclosureBadge
              icon="balance"
              label={label}
              tone={tone}
            />
          </div>
          <div className="text-xs text-stone-500 mt-1">
            Period {c.period} {c.year} · {row.submissionCount} employee{row.submissionCount !== 1 ? 's' : ''}
            {ready && ` · ${row.managerSubmittedCount}/${row.submissionCount} manager submitted`}
          </div>
        </div>
        <Link to={routes.hrV1Cycle(c.id, { stepHash: 4 })}>
          <Button
            variant={status === 'ready' || status === 'in_session' ? 'submit' : 'secondary'}
            icon="open_in_new"
          >
            Open calibration
          </Button>
        </Link>
      </div>

      <WorkflowStepper steps={row.workflowSteps.map((st, i) => ({
        ...st,
        // Emphasize Step 4 in this view
        status: i + 1 === 4 && st.status === 'pending' && ready ? 'active' : st.status,
      }))} compact />

      {s && s.status !== 'locked' && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
          {(['exceptional', 'exceed', 'meet', 'needs_improvement', 'unsatisfactory'] as const).map(b => {
            const row = s.distribution[b];
            return (
              <div key={b} className={[
                'rounded-lg border px-2 py-1.5',
                row.status === 'within' ? 'border-emerald-200 bg-emerald-50' :
                row.status === 'review' ? 'border-amber-200 bg-amber-50' :
                                          'border-red-200 bg-red-50',
              ].join(' ')}>
                <div className="text-[10px] uppercase tracking-wide text-stone-500">{b.replace('_', ' ')}</div>
                <div className="text-sm font-semibold text-stone-900">{row.count} <span className="text-xs font-normal text-stone-500">· {row.pct.toFixed(0)}%</span></div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

// ─── Guide — calibration start-up flow ──────────────────────────

const CalibrationGuide: React.FC = () => {
  const [open, setOpen] = React.useState(false);
  return (
    <section className="rounded-xl border border-stone-200 bg-stone-50 p-5">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="material-symbols-rounded text-indigo-700" style={{ fontSize: 20 }}>school</span>
          <h2 className="text-sm font-semibold text-stone-900">How to run a calibration session</h2>
        </div>
        <span className="material-symbols-rounded text-stone-500" style={{ fontSize: 20 }}>
          {open ? 'expand_less' : 'expand_more'}
        </span>
      </button>

      {open && (
        <ol className="mt-4 space-y-3 text-sm">
          <GuideStep n={1} title="Preparation (each manager, before session)" body="Draft a rating per direct report + write a 2–3 sentence justification + flag the ones you're unsure about." />
          <GuideStep n={2} title="Start a session in the cycle console" body="Open the cycle → Step 4 → click Start session. Adds the calibration session doc and switches the cycle to 'preparing'." />
          <GuideStep n={3} title="Compute distribution" body="System counts every employee by band (Exceptional / Exceed / Meet / NI / Unsatisfactory) and flags each band as Within / Review / Out vs. the framework targets." />
          <GuideStep n={4} title="Review the 5s & 1s panel" body='"Exceptional" must be truly exceptional, "Unsatisfactory" must be documented. Items missing evidence get a red border so you know who to chase.' />
          <GuideStep n={5} title="Check the lenient/strict manager panel" body="Managers whose average rating deviates ≥0.5 from the org average get flagged. Discuss before the session locks." />
          <GuideStep n={6} title="Adjust scores via calibrationAdjustments" body="Original raw scores are never overwritten — each adjustment is an append-only audit row (§E.6)." />
          <GuideStep n={7} title="Lock & advance to Step 5" body="Blocks when any band is 'out'. Requires typed cycle-name confirmation. Locking rewrites appraisal totals with calibrated scores." />
        </ol>
      )}
    </section>
  );
};

const GuideStep: React.FC<{ n: number; title: string; body: string }> = ({ n, title, body }) => (
  <li className="flex items-start gap-3">
    <span className="w-6 h-6 rounded-full bg-stone-900 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">
      {n}
    </span>
    <div className="min-w-0">
      <div className="text-sm font-medium text-stone-900">{title}</div>
      <div className="text-xs text-stone-600 mt-0.5 leading-relaxed">{body}</div>
    </div>
  </li>
);

function priorityOf(r: Row): number {
  const s = r.session;
  if (!s && r.managerSubmittedCount > 0) return 0;      // ready to start
  if (s?.status === 'in_session')        return 1;      // active session
  if (s?.status === 'preparing')         return 2;      // preparing
  if (!s)                                 return 3;     // waiting
  if (s.status === 'locked')              return 4;     // done
  return 5;
}

export default HrV1CalibrationIndex;
